import { z } from "zod";

/**
 * Notification types are intentionally declared in one place. The API keeps
 * `type` open-ended so a newly deployed producer cannot break an older app;
 * the client resolves unknown values to the safe fallback contract below.
 */
export const NOTIFICATION_TYPE_REGISTRY = [
  {
    type: "medication_reminder",
    destination: "medication",
    icon: "pill",
    priority: "action",
    titleBodyOwner: "producer",
    preferenceKey: "medicationsEnabled",
    channels: ["in_app", "push", "texting_agent"],
    privacy: "clinical",
    expiry: "after_occurrence",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "appointment_reminder",
    destination: "appointment",
    icon: "calendar",
    priority: "action",
    titleBodyOwner: "producer",
    preferenceKey: "appointmentsEnabled",
    channels: ["in_app", "push", "texting_agent"],
    privacy: "clinical",
    expiry: "after_appointment",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "test_result_available",
    destination: "test_result",
    icon: "beaker",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "testResults",
    channels: ["in_app", "push", "texting_agent"],
    privacy: "clinical",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "document_request",
    destination: "document_request",
    icon: "clipboard",
    priority: "action",
    titleBodyOwner: "producer",
    preferenceKey: "documents",
    channels: ["in_app", "push", "texting_agent"],
    privacy: "sensitive",
    expiry: "product_defined",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "document_update",
    destination: "document",
    icon: "document",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "documents",
    channels: ["in_app", "push"],
    privacy: "sensitive",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "care_team_message",
    destination: "assistant",
    icon: "team",
    priority: "message",
    titleBodyOwner: "producer",
    preferenceKey: "careTeamMessages",
    channels: ["in_app", "push"],
    privacy: "sensitive",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "partner_invitation_sent",
    destination: "partner",
    icon: "handshake",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: null,
    // Until the server exposes a persisted partner audience/preference and a
    // lock-screen-safe projection, keep relationship events in the durable
    // in-app center rather than sending unredacted producer copy to push.
    channels: ["in_app"],
    privacy: "sensitive",
    expiry: "product_defined",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "partner_invitation_accepted",
    destination: "partner",
    icon: "handshake",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: null,
    channels: ["in_app"],
    privacy: "sensitive",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "partner_connected",
    destination: "partner",
    icon: "handshake",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: null,
    channels: ["in_app"],
    privacy: "sensitive",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "clinic_update",
    destination: "clinic",
    icon: "stethoscope",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "clinicUpdates",
    channels: ["in_app", "push"],
    privacy: "sensitive",
    expiry: "product_defined",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "coverage_status",
    destination: "coverage",
    icon: "shield",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "coverageStatus",
    channels: ["in_app", "push"],
    privacy: "sensitive",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "payment_status",
    destination: "payment",
    icon: "money",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "payments",
    channels: ["in_app", "push"],
    privacy: "sensitive",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "cycle_update",
    destination: "cycle",
    icon: "chart",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "cycleUpdates",
    channels: ["in_app", "push"],
    privacy: "clinical",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "period_prediction",
    destination: "cycle",
    icon: "heart",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "cycleUpdates",
    channels: ["in_app", "push"],
    privacy: "clinical",
    expiry: "product_defined",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "care_task",
    destination: "typed",
    icon: "checkbox",
    priority: "action",
    titleBodyOwner: "producer",
    preferenceKey: "careTasks",
    channels: ["in_app", "push"],
    privacy: "sensitive",
    expiry: "product_defined",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "system",
    destination: "none",
    icon: "gear",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: null,
    channels: ["in_app"],
    privacy: "low",
    expiry: "never",
    readSemantics: "manual_only",
    quickActions: [],
  },
  {
    type: "badge_unlocked",
    destination: "badge",
    icon: "sparkle",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: null,
    channels: ["in_app"],
    privacy: "low",
    expiry: "never",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
  {
    type: "cycle_insight",
    destination: "cycle",
    icon: "lightbulb",
    priority: "update",
    titleBodyOwner: "producer",
    preferenceKey: "cycleUpdates",
    channels: ["in_app", "push"],
    privacy: "clinical",
    expiry: "product_defined",
    readSemantics: "acknowledge_on_navigation",
    quickActions: [],
  },
] as const;

export const SUPPORTED_NOTIFICATION_TYPES = NOTIFICATION_TYPE_REGISTRY.map(
  ({ type }) => type,
);

export type SupportedNotificationType =
  (typeof NOTIFICATION_TYPE_REGISTRY)[number]["type"];
export type NotificationDestination =
  (typeof NOTIFICATION_TYPE_REGISTRY)[number]["destination"];
export type NotificationPriority =
  (typeof NOTIFICATION_TYPE_REGISTRY)[number]["priority"];
export type NotificationPrivacy =
  (typeof NOTIFICATION_TYPE_REGISTRY)[number]["privacy"];
export type NotificationExpiry =
  (typeof NOTIFICATION_TYPE_REGISTRY)[number]["expiry"];
export type NotificationReadSemantics =
  (typeof NOTIFICATION_TYPE_REGISTRY)[number]["readSemantics"];
export type NotificationChannel =
  (typeof NOTIFICATION_TYPE_REGISTRY)[number]["channels"][number];

export type NotificationPreferenceKey =
  | "medicationsEnabled"
  | "appointmentsEnabled"
  | "testResults"
  | "coverageStatus"
  | "cycleUpdates"
  | "documents"
  | "careTeamMessages"
  | "clinicUpdates"
  | "payments"
  | "careTasks";

export const NOTIFICATION_LINK_TYPES = [
  "assistant",
  "texting_agent",
  "medical_history",
  "medication",
  "appointment",
  "document",
  "document_request",
  "test_result",
  "lab_result",
  "clinic",
  "coverage",
  "payment",
  "cycle",
  "partner",
  "badge",
] as const;

export type NotificationLinkType = (typeof NOTIFICATION_LINK_TYPES)[number];

const notificationDataShape = z
  .object({
    linkType: z.string().optional(),
    linkId: z.string().optional(),
    sourceEventId: z.string().optional(),
    occurrenceId: z.string().optional(),
    medicationId: z.string().optional(),
    appointmentId: z.string().optional(),
    documentId: z.string().optional(),
    requestId: z.string().optional(),
    resultId: z.string().optional(),
    clinicId: z.string().optional(),
    cycleId: z.string().optional(),
    invitationId: z.string().optional(),
    partnerPatientId: z.string().optional(),
    inviterName: z.string().optional(),
    partnerName: z.string().optional(),
    scheduledAtUtc: z.string().optional(),
    doseContext: z.string().optional(),
  })
  .passthrough();

export type NotificationData = z.infer<typeof notificationDataShape>;

export function parseNotificationData(value: unknown): NotificationData {
  const parsed = notificationDataShape.safeParse(value);
  return parsed.success ? parsed.data : {};
}

export function getNotificationLinkType(
  value: unknown,
): NotificationLinkType | undefined {
  const linkType = parseNotificationData(value).linkType;
  return (NOTIFICATION_LINK_TYPES as readonly string[]).includes(linkType ?? "")
    ? (linkType as NotificationLinkType)
    : undefined;
}

export function getNotificationContract(type: string) {
  return (
    NOTIFICATION_TYPE_REGISTRY.find((entry) => entry.type === type) ??
    UNKNOWN_NOTIFICATION_CONTRACT
  );
}

/** Unknown types remain visible, but never inherit sensitive actions/channels. */
export const UNKNOWN_NOTIFICATION_CONTRACT = {
  type: "unknown",
  destination: "none",
  icon: "gear",
  priority: "update",
  titleBodyOwner: "producer",
  preferenceKey: null,
  channels: ["in_app"],
  privacy: "sensitive",
  expiry: "never",
  readSemantics: "manual_only",
  quickActions: [],
} as const;

export type NotificationProducerEvent = {
  userId: string;
  type: SupportedNotificationType;
  sourceEventId: string;
  occurredAt: string;
  data: NotificationData;
  title: string;
  body?: string | null;
  expiresAt?: string | null;
};

/**
 * Producer idempotency must be stable across retries and delivery channels.
 * The backend should enforce uniqueness on this value before inserting a row.
 */
export function getNotificationIdempotencyKey(
  event: NotificationProducerEvent,
) {
  return `${event.userId}:${event.type}:${event.sourceEventId}`;
}
